import React,
{
    useLayoutEffect,
    useState
}
from 'react';

// Very small client-side router to avoid adding react-router-dom dependency.
// Uses the browser history API and listens for popstate. Routes are simple
// path strings like '/main'. This component renders the child whose
// `path` prop matches the current location.pathname.
export function normalizePath(path)
{
    const rawPath = typeof path === 'string' && path ? path : '/';
    if (rawPath === '/') return '/';

    const normalizedPath = rawPath.replace(/\/+$/, '');
    return normalizedPath || '/';
}

export function matchesRoutePath(currentPath, routePath, allowChildRoutes = false)
{
    const normalizedCurrentPath = normalizePath(currentPath);
    const normalizedRoutePath = normalizePath(routePath);

    if (normalizedCurrentPath === normalizedRoutePath) return true;
    if (!allowChildRoutes || normalizedRoutePath === '/') return false;

    return normalizedCurrentPath.startsWith(`${normalizedRoutePath}/`);
}

export function Route(
{
    path,
    children,
    allowChildRoutes = false
})
{
    const normalizedRoutePath = normalizePath(path);
    const [match, setMatch] = useState(matchesRoutePath(window.location.pathname, normalizedRoutePath, allowChildRoutes));

    useLayoutEffect(() =>
    {
        const syncMatch = () => setMatch(matchesRoutePath(window.location.pathname, normalizedRoutePath, allowChildRoutes));
        syncMatch();
        window.addEventListener('popstate', syncMatch);
        return () => window.removeEventListener('popstate', syncMatch);
    }, [allowChildRoutes, normalizedRoutePath]);

    return match ? <>
    {
        children
    } </> : null;
}

export function navigate(to)
{
    const targetPath = normalizePath(to);
    const currentPath = normalizePath(window.location.pathname);
    if (currentPath === targetPath) return;
    // include previous path in history state so pages can react to origin
    try
    {
        const from = currentPath;
        window.history.pushState(
        {
            from
        }, '', targetPath);
    }
    catch (e)
    {
        // fallback without state if pushState fails
        window.history.pushState(
        {}, '', targetPath);
    }
    // dispatch event so listeners update
    window.dispatchEvent(new PopStateEvent('popstate'));
}

export function replaceNavigate(to)
{
    const targetPath = normalizePath(to);
    const currentPath = normalizePath(window.location.pathname);
    if (currentPath === targetPath) return;
    try
    {
        const from = currentPath;
        window.history.replaceState(
        {
            from
        }, '', targetPath);
    }
    catch (e)
    {
        window.history.replaceState(
        {}, '', targetPath);
    }
    window.dispatchEvent(new PopStateEvent('popstate'));
}

export default function Router(
{
    children
})
{
    // simple wrapper that re-renders on popstate so Route components update
    const [, setTick] = useState(0);
    useLayoutEffect(() =>
    {
        try
        {
            const rawPath = window.location.pathname || '/';
            const normalizedPath = normalizePath(rawPath);
            if (rawPath !== normalizedPath)
            {
                const search = window.location.search || '';
                const hash = window.location.hash || '';
                const currentState = window.history.state ||
                {};
                window.history.replaceState(currentState, '', `${normalizedPath}${search}${hash}`);
            }
        }
        catch (e)
        {}

        const onPop = () => setTick((t) => t + 1);
        onPop();
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);
    return <>
    {
        children
    } </>;
}
